import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../../providers/auth_provider';

const INITIAL_FORM_STATE = {
    // title: '',
    description: '',
    soundtrack_volume: '',
    frequency_volume: '',
    category_id: '',
    status: 'active',
    subscription_type: '',
    frequency_audio: null,
    music_audio: null,
    thumbnail: null,
    free_start_time: '',
    free_end_time: '',
    no_of_days_for_free: '',
};

const VolumeControl = ({ icon, label, value, onChange, name }) => (
    <div className="volume-control p-3 bg-opacity-25 rounded-3">
        <div className="d-flex align-items-center mb-2">
            <i className={`ri-${icon} me-2`}></i>
            <span className="text-dark">{label} {Math.floor(value)}</span>
        </div>
        <input
            type="range"
            className="form-range"
            min="0"
            max="100"
            step="0.1"
            name={name}
            value={value}
            onChange={onChange}
            style={{ height: '4px' }}
        />
    </div>
);


export default function EditFrequency({ frequencyData }) {
    const { token } = useAuth();
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const frequencyAudioRef = useRef(null);
    const musicAudioRef = useRef(null);
    const [formData, setFormData] = useState(INITIAL_FORM_STATE);
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value, files } = e.target;

        if (files) {
            const file = files[0];
            setFormData(prev => ({ ...prev, [name]: file }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));

            // Update audio volume
            if (name === 'frequency_volume' && frequencyAudioRef.current) {
                frequencyAudioRef.current.volume = parseInt(value) / 100;
            }
            if (name === 'soundtrack_volume' && musicAudioRef.current) {
                musicAudioRef.current.volume = parseInt(value) / 100;
            }
        }

        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = {
            category_id: 'Category is required',
            subscription_type: 'Subscription type is required',
        };

        Object.entries(requiredFields).forEach(([field, message]) => {
            if (!formData[field]) {
                newErrors[field] = message;
            }
        });

        // Check if subscription type is "free" and require start and end time
        if (formData.subscription_type === 'free') {
            if (!formData.no_of_days_for_free || formData.no_of_days_for_free === 0) {
                newErrors.no_of_days_for_free = 'Free Time is required';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const fetchData = async () => {
        try {
            const [categoriesResponse, frequencyResponse] = await Promise.all([
                fetch(`${process.env.REACT_APP_API_URL}/category/all`, {
                    headers: {
                        'Signature': process.env.REACT_APP_API_SIGNATURE,
                        'Authorization': `Bearer ${token}`
                    }
                }),
                frequencyData?.id ? fetch(`${process.env.REACT_APP_API_URL}/frequency/${frequencyData.id}`, {
                    headers: {
                        'Signature': process.env.REACT_APP_API_SIGNATURE,
                        'Authorization': `Bearer ${token}`
                    }
                }) : null
            ]);

            const categoriesData = await categoriesResponse.json();
            setCategories(categoriesData.categories);

            if (frequencyResponse) {
                const frequencyData = await frequencyResponse.json();
                setFormData({
                    description: '',
                    soundtrack_volume: frequencyData?.frequency?.soundtrack_volume,
                    frequency_volume: frequencyData?.frequency?.frequency_volume,
                    category_id: frequencyData?.frequency?.category_id,
                    status: frequencyData?.frequency?.status,
                    subscription_type: frequencyData?.frequency?.subscription_type,
                    frequency_audio: null,
                    music_audio: null,
                    thumbnail: null,
                    free_start_time: '',
                    free_end_time: '',
                    no_of_days_for_free: frequencyData?.frequency?.frequency_details[0]?.no_of_days_for_free,
                });
            }
        } catch (error) {
            toast.error('Error loading data');
            console.error('Error fetching data:', error);
        }
    };

    useEffect(() => {
        if (frequencyData?.id) {
            fetchData();
        }
        // return () => {
        //     Object.values(audioPreview).forEach(url => {
        //         if (url) URL.revokeObjectURL(url);
        //     });
        // };
    }, [frequencyData?.id]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error('Please fix the form errors');
            return;
        }

        const formDataToSend = new FormData();
        Object.entries(formData).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
                formDataToSend.append(key, value);
            }
        });

        try {
            setLoading(true);
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/frequency/update/${frequencyData.id}`,
                {
                    method: 'PUT',
                    headers: {
                        'Signature': process.env.REACT_APP_API_SIGNATURE,
                        'Authorization': `Bearer ${token}`
                    },
                    body: formDataToSend
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Update failed');
            }

            toast.success('Frequency updated successfully');
            fetchData();
            window.location.reload();
            document.getElementById('closeModal').click();
        } catch (error) {
            toast.error(error.message || 'Error updating frequency');
            console.error('Update error:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="offcanvas offcanvas-end w-50" tabIndex={-1} id="offcanvasEditFrequency">
            <div className="offcanvas-header border-bottom">
                <h5 id="offcanvasEditFrequencyLabel" className="offcanvas-title">
                    Edit Frequency
                </h5>
                <button
                    type="button"
                    id="closeModal"
                    className="btn-close text-reset"
                    data-bs-dismiss="offcanvas"
                    aria-label="Close"
                />
            </div>

            <div className="offcanvas-body mx-0 flex-grow-0 h-100">
                <form className="add-new-user pt-0" onSubmit={handleSubmit}>
                    <div className="form-floating form-floating-outline mb-4">
                        <select
                            name="category_id"
                            className={`form-select text-capitalize ${errors.category_id ? 'is-invalid' : ''}`}
                            value={formData.category_id}
                            onChange={handleChange}
                        >
                            <option value="">Select Category</option>
                            {categories.map((cat) => (
                                <option key={cat.id} className="text-capitalize" value={cat.id}>{cat.name}</option>
                            ))}
                        </select>
                        <label>Category</label>
                        {errors.category_id && <div className="invalid-feedback">{errors.category_id}</div>}
                    </div>

                    <div className="form-floating form-floating-outline mb-4">
                        <input
                            type="file"
                            className={`form-control ${errors.frequency_audio ? 'is-invalid' : ''}`}
                            name="frequency_audio"
                            onChange={handleChange}
                            accept="audio/*"
                        />
                        <label>Upload Frequency Audio</label>
                        {errors.frequency_audio && <div className="invalid-feedback">{errors.frequency_audio}</div>}
                    </div>

                    <div className="row pb-4">
                        <div className="col-md-6">
                            <VolumeControl
                                icon="volume-up-line"
                                label="Frequency Volume"
                                value={formData.frequency_volume}
                                onChange={handleChange}
                                name="frequency_volume"
                            />
                        </div>
                    </div>

                    <div className="row pb-4">
                        <div className="col-md-6">
                            <VolumeControl
                                icon="volume-up-line"
                                label="Music Volume"
                                value={formData.soundtrack_volume}
                                onChange={handleChange}
                                name="soundtrack_volume"
                            />
                        </div>
                    </div>

                    <div className="form-floating form-floating-outline mb-4">
                        <select
                            name="subscription_type"
                            className={`form-select ${errors.subscription_type ? 'is-invalid' : ''}`}
                            value={formData.subscription_type}
                            onChange={handleChange}
                        >
                            <option value="">Select Subscription Type</option>
                            <option value="free">Free</option>
                            <option value="paid">Paid</option>
                        </select>
                        <label>Subscription Type</label>
                        {errors.subscription_type && <div className="invalid-feedback">{errors.subscription_type}</div>}
                    </div>

                    <div className="form-floating form-floating-outline mb-4">
                        <select
                            name="status"
                            className={`form-select ${errors.status ? 'is-invalid' : ''}`}
                            value={formData.status}
                            onChange={handleChange}
                        >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                        <label>Status</label>
                        {errors.status && <div className="invalid-feedback">{errors.status}</div>}
                    </div>

                    {formData.subscription_type === 'free' ? (
                        <div className="d-flex gap-5 mb-5">
                            <div className="form-floating form-floating-outline mb-5 w-100">

                                <select className={
                                    `form-select ${errors.no_of_days_for_free ? 'is-invalid' : ''}`}
                                    name="no_of_days_for_free"
                                    value={formData.no_of_days_for_free}
                                    onChange={handleChange}>
                                    <option value="">Select</option>
                                    {[...Array(31)].map((_, i) => (
                                        <option key={i + 1} value={i + 1}>
                                            {i + 1} Days
                                        </option>
                                    ))}
                                </select>
                                <label>No of Days For Free</label>
                                {errors.no_of_days_for_free && <div className="invalid-feedback">{errors.no_of_days_for_free}</div>}
                            </div>

                        </div>
                    ) : null}

                    <div className="text-center mt-4">
                        <button type="submit" className="btn btn-primary" disabled={loading}>
                            {loading ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
